/*
 * Copyright (c) 2019. ProVision Media Group Ltd. <https://provision.bg>
 * Venelin Iliev <http://veneliniliev.com>
 */

import { Injectable } from '@angular/core';
import { ResourceInterface } from '@app/shared/models/resource.interface';
import { HttpClient } from '@angular/common/http';
import { ApiQuery } from '@app/core/http/api-query';
import { Observable } from 'rxjs';
import { PaginatedResponse } from '@app/shared/models/paginated-response.model';
import { map } from 'rxjs/operators';
import { HttpResponse, Logger } from '@app/core';
import { Model } from '@app/shared/models/model.model';
import { City } from '@app/settings/cities/cities.model';

const log = new Logger('EventGroupSelectComponent');

@Injectable({
  providedIn: 'root',
})
export class CitiesService implements ResourceInterface {
  constructor(private http: HttpClient) {}

  index(query: ApiQuery = new ApiQuery()): Observable<PaginatedResponse> {
    log.debug('index');
    return this.http.get('v1/cities', query.buildGetOptions()).pipe(
      map((response: HttpResponse) => {
        response.data = response.data.map((city: City) => {
          return new City().deserialize(city);
        });

        return new PaginatedResponse().deserialize(response);
      })
    );
  }

  update(city: City): Observable<City> {
    return this.http.put('v1/cities/' + city.id, city).pipe(
      map((response: HttpResponse) => {
        return new City().deserialize(response.data);
      })
    );
  }

  store(city: City): Observable<City> {
    return this.http.post('v1/cities', city).pipe(
      map((response: HttpResponse) => {
        return new City().deserialize(response.data);
      })
    );
  }

  /**
   * Check if cities with the exact same name already exist in the given country.
   * The API name filter is a partial match, so results are narrowed down
   * client-side to exact (case-insensitive) matches only.
   */
  findDuplicates(name: string, countryId: number): Observable<City[]> {
    const query = new ApiQuery().addFilter('name', name).addFilter('country_id', countryId).setLimit(0);
    return this.index(query).pipe(
      map((response: PaginatedResponse) => {
        const normalizedName = name.trim().toLowerCase();
        return (response.data as City[]).filter((city: City) => {
          return city.name && city.name.trim().toLowerCase() === normalizedName;
        });
      })
    );
  }

  show(id: number, query: ApiQuery = new ApiQuery()): Observable<Model> {
    return this.http.get('v1/cities/' + id, query.buildGetOptions()).pipe(
      map((response: HttpResponse) => {
        return new City().deserialize(response.data);
      })
    );
  }

  destroy(id: number): Observable<any> {
    return this.http.delete('v1/cities/' + id);
  }

  updateTranslations(city: City, translations: any): Observable<any> {
    return this.http.put('v1/cities/translations/' + city.id, translations).pipe(
      map((response: HttpResponse) => {
        return response;
      })
    );
  }
}
